import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

interface UseFocusTrapOptions {
  /** Whether the trap is active. Toggle to false to release. */
  enabled: boolean;
  /**
   * Optional explicit element to focus on enable. When omitted, the
   * first focusable child of the trap container is focused.
   */
  initialFocus?: HTMLElement | null;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

function getFocusableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

// Module-level trap stack — the topmost active trap handles Tab; lower
// traps stay dormant until their turn at the top. Mirrors the escape
// stack in utils/escapeStack.ts and solves the nested-overlay focus
// fight: without this, both an outer AdaptivePanel's trap and an inner
// ConfirmDialog's trap would each receive Tab and the outer would snap
// focus back inside its container (ejecting the user from the dialog
// when its content sits in a portal outside the outer's DOM tree).
const trapStack: HTMLElement[] = [];

/**
 * Focus trap per ADR-0077. Attach the returned ref to the overlay's
 * content container. While `enabled` is true:
 *
 * - First focusable child (or `initialFocus`) receives focus on enable.
 * - Tab / Shift-Tab cycle within the trap — focus can't escape.
 * - On disable, the snapshotted "previously focused" element is restored.
 * - Stacking: only the topmost trap handles Tab; nested overlays
 *   correctly own their own focus.
 *
 * No-op on native — RN modals already isolate focus and there's no
 * document-level Tab loop to corral.
 */
export function useFocusTrap<T extends HTMLElement = HTMLElement>({
  enabled,
  initialFocus,
}: UseFocusTrapOptions) {
  const ref = useRef<T | null>(null);
  // Stash so the close handler can restore even after the trap unmounts.
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (Platform.OS !== 'web') return;
    if (typeof document === 'undefined') return;
    const container = ref.current;
    if (!container) return;

    // Snapshot the previously focused element so it can regain focus
    // when the trap releases.
    previousFocusRef.current = (document.activeElement as HTMLElement | null) ?? null;

    // Focus the initial target or the first focusable descendant.
    const target = initialFocus ?? getFocusableWithin(container)[0] ?? container;
    // The container itself may not be focusable — give it tabIndex=-1
    // so focus() works even when there are no focusable descendants.
    if (target === container && !container.hasAttribute('tabindex')) {
      container.setAttribute('tabindex', '-1');
    }
    target.focus({ preventScroll: true });

    // Register on the trap stack so nested overlays can take precedence.
    trapStack.push(container);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      // Topmost-only — dormant when another trap is on top.
      if (trapStack[trapStack.length - 1] !== container) return;
      const focusable = getFocusableWithin(container);
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      // Outside the trap? Snap back inside.
      if (active && !container.contains(active)) {
        e.preventDefault();
        first.focus({ preventScroll: true });
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const idx = trapStack.lastIndexOf(container);
      if (idx >= 0) trapStack.splice(idx, 1);
      // Restore focus when the trap releases — but only if the
      // previously focused element is still in the document and
      // currently focusable.
      const prev = previousFocusRef.current;
      previousFocusRef.current = null;
      if (prev && document.body.contains(prev)) {
        try {
          prev.focus({ preventScroll: true });
        } catch {
          /* element no longer focusable */
        }
      }
    };
  }, [enabled, initialFocus]);

  return ref;
}
